import { isUndefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectMarketAdjustedSalePrice,
  selectMarketAdjustedSalePrices,
} from './calculatedMarketAdjustmentSelectors/selectMarketAdjustedSalePrice.ts';
import { selectCompSequence } from './orderSelectors.ts';
import { selectSiteValue, selectSiteValues } from './selectSiteValue.ts';
import { selectSubjectValue } from './selectSubjectValue.ts';

const calculateSiteValueRatio = (
  siteValue?: ReturnType<typeof selectSiteValue>,
  marketAdjustedSalesPrice?: ReturnType<typeof selectMarketAdjustedSalePrice>,
) => {
  if (
    isUndefined(siteValue) ||
    isUndefined(marketAdjustedSalesPrice) ||
    marketAdjustedSalesPrice.unitAmount === 0
  ) {
    return undefined;
  }

  return siteValue.ratio(marketAdjustedSalesPrice);
};

function formatSiteValueRatio(siteValueRatio: number | undefined) {
  if (isUndefined(siteValueRatio) || siteValueRatio === Infinity) {
    return undefined;
  }

  return `${Math.round(siteValueRatio * 100)}%`;
}

const selectSiteValueRatio = createSelector(
  [selectSiteValue, selectMarketAdjustedSalePrice],
  calculateSiteValueRatio,
);

const selectSubjectSiteValueRatio = createSelector(
  [selectSiteValue, selectSubjectValue],
  calculateSiteValueRatio,
);

export const selectSubjectSiteValueRatioDisplay = createSelector(
  [selectSubjectSiteValueRatio],
  (subjectSiteValueRatio) => formatSiteValueRatio(subjectSiteValueRatio),
);

export const selectSiteValueRatios = createSelector(
  [selectSiteValues, selectMarketAdjustedSalePrices, selectCompSequence, selectSubjectValue],
  (siteValues, marketAdjustedSalesPrices, compSequence, subjectValue) => {
    const properties = ['subject', ...compSequence];

    return Object.fromEntries(
      properties.map((propID) => [
        propID,
        calculateSiteValueRatio(
          siteValues[propID],
          propID === 'subject' ? subjectValue : marketAdjustedSalesPrices[propID],
        ),
      ]),
    );
  },
);

export const selectSiteValueRatioDisplay = createSelector(
  [selectSiteValueRatio],
  (siteValueRatio) => formatSiteValueRatio(siteValueRatio),
);

export const selectSiteValueRatiosDisplay = createSelector(
  [selectSiteValueRatios],
  (siteValueRatios) => {
    return Object.fromEntries(
      Object.entries(siteValueRatios).map(([propID, siteValueRatio]) => {
        if (isUndefined(siteValueRatio) || siteValueRatio === Infinity) {
          return [propID, undefined];
        }
        return [propID, `${Math.round(siteValueRatio * 100)}%`];
      }),
    );
  },
);
